import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
import AuthLocaleBar, {
  authSiblingPath,
} from "../../Components/Common/AuthLocaleBar";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { loginUser, resetLoginFlag, loginWithGoogle } from "../../slices/thunks";
import { getBrandLogos } from "../../helpers/brandPortal";
import { createSelector } from "reselect";
import GoogleSignInButton from "../../Components/Common/GoogleSignInButton";

const Login = (props: any) => {
  const { t, i18n } = useTranslation();
  const logos = getBrandLogos();
  const dispatch: any = useDispatch();

  const loginpageData = createSelector(
    (state: any) => state,
    (state) => ({
      error: state.Login.error,
      errorMsg: state.Login.errorMsg,
    })
  );

  const { error, errorMsg } = useSelector(loginpageData);
  const [passwordShow, setPasswordShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const inviteAccepted =
    new URLSearchParams(window.location.search).get("invite") === "accepted";

  const handleGoogleCredential = (idToken: string) => {
    setGoogleBusy(true);
    setLoader(true);
    dispatch(loginWithGoogle(idToken, props.router.navigate));
  };

  // Campos vazios de propósito: credenciais DEV não ficam na UI pública (prod no ar).
  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("auth.login.validation.emailInvalid"))
        .required(t("auth.login.validation.emailRequired")),
      password: Yup.string().required(
        t("auth.login.validation.passwordRequired")
      ),
    }),
    onSubmit: (values) => {
      setLoader(true);
      dispatch(loginUser(values, props.router.navigate));
    },
  });

  useEffect(() => {
    validation.setFormikState((prev: any) => ({
      ...prev,
      validationSchema: Yup.object({
        email: Yup.string()
          .email(t("auth.login.validation.emailInvalid"))
          .required(t("auth.login.validation.emailRequired")),
        password: Yup.string().required(
          t("auth.login.validation.passwordRequired")
        ),
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  useEffect(() => {
    if (errorMsg) {
      setLoader(false);
      setGoogleBusy(false);
      const timer = setTimeout(() => {
        dispatch(resetLoginFlag());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, errorMsg]);

  document.title = t("auth.login.documentTitle");

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4">
                  <Link
                    to={authSiblingPath("/login")}
                    className="d-inline-block auth-logo"
                  >
                    <img src={logos.dark} alt={logos.alt} height="20" />
                  </Link>
                  <p className="mt-3 fs-15 fw-medium text-muted">
                    {t("auth.brandSubtitle")}
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <AuthLocaleBar />
                    <div className="text-center mt-2">
                      <h5 className="text-primary">{t("auth.login.title")}</h5>
                      <p className="text-muted">{t("auth.login.subtitle")}</p>
                    </div>

                    {errorMsg && error ? (
                      <Alert color="danger" className="mt-3" role="alert">
                        {String(error)}
                      </Alert>
                    ) : null}
                    {inviteAccepted ? (
                      <Alert color="success" className="mt-3" role="status">
                        {t("auth.login.inviteAccepted")}
                      </Alert>
                    ) : null}

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            {t("auth.login.email")}
                          </Label>
                          <Input
                            name="email"
                            id="email"
                            className="form-control"
                            placeholder={t("auth.login.emailPlaceholder")}
                            type="email"
                            autoComplete="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label" htmlFor="password-input">
                            {t("auth.login.password")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              id="password-input"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5"
                              placeholder={t("auth.login.passwordPlaceholder")}
                              autoComplete="current-password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !!(
                                  validation.touched.password &&
                                  validation.errors.password
                                )
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                              aria-label={t("auth.login.togglePassword")}
                            >
                              <i className="ri-eye-fill align-middle" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            disabled={loader || googleBusy}
                            className="btn btn-success w-100"
                            type="submit"
                          >
                            {loader && !googleBusy ? (
                              <Spinner size="sm" className="me-2">
                                …
                              </Spinner>
                            ) : null}
                            {t("auth.login.submit")}
                          </Button>
                        </div>

                        <GoogleSignInButton
                          busy={loader || googleBusy}
                          enableOneTap
                          onCredential={handleGoogleCredential}
                        />
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    {t("auth.login.noAccount")}{" "}
                    <Link
                      to={authSiblingPath("/register")}
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {t("auth.login.createAccount")}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
